import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { absoluteUrl } from "@/lib/uploads";
import { Heart, Sparkles, ShoppingBag, ArrowLeft, X, Crown, Users, Trophy, DollarSign } from "lucide-react";
import { toast } from "sonner";

const TIP_AMOUNTS = [5, 10, 25, 50];

export default function PublicProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: viewer } = useAuth();
  const [data, setData] = useState(null);
  const [tipOpen, setTipOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [tiers, setTiers] = useState({});
  const [subStats, setSubStats] = useState({ active_subscribers: 0, by_tier: {} });
  const [milestones, setMilestones] = useState([]);
  const [amount, setAmount] = useState(10);
  const [fromName, setFromName] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/profile/public/${userId}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
    axios.get(`${API}/subscribe/tiers`).then((r) => setTiers(r.data)).catch(() => {});
    axios.get(`${API}/u/${userId}/subscribers`).then((r) => setSubStats(r.data)).catch(() => {});
    axios.get(`${API}/u/${userId}/milestones`).then((r) => setMilestones(r.data.milestones || [])).catch(() => {});
  }, [userId]);

  const sendTip = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/tip/checkout`, {
        user_id: userId,
        amount: parseFloat(amount),
        origin_url: window.location.origin,
        from_name: fromName || "Anonymous",
        message,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tip failed");
      setBusy(false);
    }
  };

  const subscribe = async (tier) => {
    if (!viewer) {
      toast.error("Sign in to subscribe");
      navigate("/", { replace: false });
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/subscribe/checkout`, {
        creator_user_id: userId,
        tier,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscription failed");
      setBusy(false);
    }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Loading…</div>;
  if (data.error) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Operator not found.</div>;

  const { user, posts, products } = data;

  return (
    <div className="min-h-screen pb-20">
      <header className="sticky top-0 z-30 glass-heavy">
        <div className="max-w-[1200px] mx-auto px-6 py-3 flex items-center gap-3">
          <button data-testid="public-back" onClick={() => navigate(-1)} className="w-9 h-9 rounded-full glass flex items-center justify-center"><ArrowLeft size={16} /></button>
          <div className="font-display font-bold">NEXUS · public profile</div>
          <button data-testid="open-tip" onClick={() => setTipOpen(true)} className="ml-auto nexus-btn-secondary text-sm flex items-center gap-2">
            <Heart size={14} /> Tip
          </button>
          <button data-testid="open-subscribe" onClick={() => setSubOpen(true)} className="nexus-btn-primary text-sm flex items-center gap-2">
            <Crown size={14} /> Subscribe
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-10 space-y-8">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-heavy rounded-3xl p-8 grain text-center">
          <div className="w-24 h-24 mx-auto rounded-3xl bg-gradient-to-br from-[#00E5FF] to-[#7B2CBF] flex items-center justify-center font-display font-black text-4xl text-white glow-ring animate-pulse-glow">
            {user.name?.[0]?.toUpperCase()}
          </div>
          <h1 className="font-display text-4xl font-bold mt-5">{user.name}</h1>
          <div className="text-xs uppercase tracking-[0.25em] text-[#00E5FF] mt-1">operator · nexus</div>
          <p className="text-sm text-[#64748B] mt-3 max-w-md mx-auto">Building a digital empire on a living intelligence network.</p>
          {subStats.active_subscribers > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-xs">
              <Users size={12} className="text-[#F5C542]" />
              <span className="text-[#F5C542] font-bold">{subStats.active_subscribers}</span>
              <span className="text-[#64748B]">active patron{subStats.active_subscribers === 1 ? "" : "s"}</span>
            </div>
          )}
          {milestones.length > 0 && (
            <div
              data-testid="milestones-wall"
              className="mt-5 flex flex-wrap items-center justify-center gap-2"
            >
              {milestones.map((m) => {
                const isRevenue = m.kind === "revenue";
                const Icon = isRevenue ? DollarSign : Trophy;
                const accent = isRevenue ? "#00E5FF" : "#F5C542";
                return (
                  <motion.div
                    key={`${m.kind}-${m.level}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Link
                      to={`/m/${userId}/${m.kind}/${m.level}`}
                      data-testid={`milestone-badge-${m.kind}-${m.level}`}
                      className="inline-flex items-center gap-1.5 glass px-3 py-1.5 rounded-full text-[11px] hover:bg-slate-100 transition-all"
                      style={{ borderWidth: 1, borderStyle: "solid", borderColor: `${accent}4D` }}
                      title={`Open shareable card${m.reached_at ? ` · Reached ${new Date(m.reached_at).toLocaleDateString()}` : ""}`}
                    >
                      <Icon size={11} style={{ color: accent }} />
                      <span className="text-[#0F172A] font-semibold tracking-wide">
                        {m.label}
                      </span>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>

        {posts.length > 0 && (
          <section data-testid="public-posts">
            <div className="flex items-center gap-2 mb-4"><Sparkles size={16} className="text-[#00E5FF]" /><div className="font-display font-bold">Recent signals</div></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {posts.map((p) => (
                <div key={p.id} className="glass rounded-2xl p-4">
                  {p.image_url && <img src={absoluteUrl(p.image_url)} alt="" className="rounded-xl w-full h-48 object-cover mb-3" />}
                  {p.video_url && <video src={absoluteUrl(p.video_url)} controls className="rounded-xl w-full h-48 object-cover mb-3" />}
                  <p className="text-[#0F172A] whitespace-pre-wrap leading-relaxed">{p.content}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {products.length > 0 && (
          <section data-testid="public-products">
            <div className="flex items-center gap-2 mb-4"><ShoppingBag size={16} className="text-[#F5C542]" /><div className="font-display font-bold">Storefront</div></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {products.map((p) => (
                <div key={p.id} className="glass rounded-2xl overflow-hidden">
                  {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-32 object-cover" />}
                  <div className="p-3">
                    <div className="font-display font-semibold text-sm line-clamp-1">{p.title}</div>
                    <div className="text-[#00E5FF] font-bold mt-1">${p.price.toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {subOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.5)", backdropFilter: "blur(10px)" }} onClick={() => setSubOpen(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className="glass-heavy rounded-3xl p-6 max-w-2xl w-full grain" data-testid="subscribe-modal">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#F5C542]">become a patron</div>
                <h3 className="font-display font-bold text-2xl">Support {user.name}</h3>
              </div>
              <button onClick={() => setSubOpen(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {Object.entries(tiers).map(([id, t]) => (
                <div key={id} className={`glass rounded-2xl p-4 flex flex-col ${id === "gold" ? "glow-ring" : ""}`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#00E5FF]">{t.label}</div>
                  <div className="font-display text-3xl font-bold mt-1">${t.price.toFixed(0)}<span className="text-sm text-[#64748B]">/mo</span></div>
                  <ul className="mt-3 space-y-1.5 text-xs text-[#64748B] flex-1">
                    {t.perks.map((p) => <li key={p}>· {p}</li>)}
                  </ul>
                  <button
                    data-testid={`subscribe-${id}`}
                    onClick={() => subscribe(id)}
                    disabled={busy}
                    className={`mt-4 ${id === "gold" ? "nexus-btn-primary" : "nexus-btn-secondary"} text-sm`}
                  >
                    {busy ? "…" : `Choose ${t.label.split(" ")[0]}`}
                  </button>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#94A3B8] mt-4 text-center">30-day patron pass · auto-expires · renewable · Stripe-secured</p>
          </motion.div>
        </div>
      )}

      {tipOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(10px)" }} onClick={() => setTipOpen(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className="glass-heavy rounded-3xl p-6 max-w-md w-full grain" data-testid="tip-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#F5C542]">support</div>
                <h3 className="font-display font-bold text-xl">Tip {user.name}</h3>
              </div>
              <button onClick={() => setTipOpen(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div className="flex gap-2 flex-wrap mb-3">
              {TIP_AMOUNTS.map((a) => (
                <button key={a} data-testid={`tip-amount-${a}`} onClick={() => setAmount(a)} className={`text-sm px-4 py-2 rounded-full ${parseFloat(amount) === a ? "glass-active text-[#00E5FF]" : "glass text-[#64748B]"}`}>${a}</button>
              ))}
              <input
                data-testid="tip-custom"
                type="number" min="1" max="500" step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="nexus-input"
                style={{ maxWidth: 110 }}
              />
            </div>
            <input
              data-testid="tip-from"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="Your name (optional)"
              className="nexus-input mb-2"
            />
            <textarea
              data-testid="tip-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="A short note (optional)"
              className="nexus-input resize-none mb-3"
            />
            <button data-testid="tip-submit" onClick={sendTip} disabled={busy || !amount} className="w-full nexus-btn-primary flex items-center justify-center gap-2">
              <Heart size={14} /> {busy ? "…" : `Send $${parseFloat(amount || 0).toFixed(2)}`}
            </button>
            <p className="text-xs text-[#94A3B8] mt-3 text-center">Secured by Stripe. No NEXUS account needed.</p>
          </motion.div>
        </div>
      )}
    </div>
  );
}
